import { deepEqual } from '../../../../utils/utils.object';

/**
 * Get index of an object within an array (works with objects)
 * @param arr
 * @param element
 */
export function getIndex(arr: Array<any>, element: any): number {
    return arr.findIndex((v) => deepEqual(v, element));
}

/**
 * Sorts, Adds an index then groups an array with a grouping function
 * @param arr array to order
 * @param getGroup group accessor
 * @param reorder whether the array should be ordered with the group
 */
export function group<T>(
    arr: Readonly<Array<T>>,
    getGroup: (v: T) => string,
    reorder = true,
): { label: string; options: (T & { _index: number })[] }[] {
    // add group value once for future use
    let mappedArr = arr.map((el) => ({ el, group: getGroup(el) }));

    if (reorder) {
        // sort alphabetically in order for the indexes to be in the right order
        mappedArr = mappedArr.sort((el1, el2) => (el1.group === el2.group ? 0 : el1.group < el2.group ? -1 : 1));
    }

    // group options by their grouping property
    const map = mappedArr.reduce((acc, { el, group }, index) => {
        if (acc.get(group)) acc.set(group, [...acc.get(group), { ...el, _index: index }]);
        else acc.set(group, [{ ...el, _index: index }]);
        return acc;
    }, new Map());

    // Convert map to array of array
    return Array.from(map).map(([k, v]) => ({ label: k, options: v }));
}

/**
 * Returns the attribute of an object according to a getter
 * If the return value is falsy, always return an empty string to avoid warning: "A component is changing a controlled input to be uncontrolled"
 * @param obj
 * @param getter
 */
export function get(obj: any, getter: any): any {
    if (getter && typeof getter === 'function') {
        return getter(obj) !== null && getter(obj) !== undefined ? getter(obj) : '';
    } else if (getter && typeof getter === 'string') {
        return obj[getter] !== null && obj[getter] !== undefined ? obj[getter] : '';
    }
    return obj !== null && obj !== undefined ? obj : '';
}

/**
 * If the obj is an array, check if the search is in it. Otherwise, checks if the search is the object
 * @param obj
 * @param search
 */
export function isOrIncludes<T>(obj: T | Array<T>, search: T) {
    if (Array.isArray(obj)) {
        return getIndex(obj, search) >= 0;
    } else {
        return deepEqual(obj, search);
    }
}

/**
 * Returns true if it is falsy, an empty array or an empty object
 * @param v
 */
export const isNullOrEmpty = (v: any | undefined) => !v || (Array.isArray(v) && !v.length);
